/**
 * BEKRA Motion — single shop-wide scroll-reveal system.
 * Loaded once globally (snippets/scripts.liquid). Replaces the previous
 * per-section duplicated <bekra-scroll-reveal> definitions.
 *
 * Two ways for a section to opt in:
 *   1. <bekra-scroll-reveal class="...">...</bekra-scroll-reveal> — wrap a
 *      block; the wrapper's own CSS drives the actual fade/translate values
 *      (existing pattern, used by ~30 BEKRA sections already).
 *   2. data-bekra-reveal="text|image|card" on any element — lighter-weight,
 *      no wrapper required, styled centrally in bekra-motion.css.
 *
 * Both variants: reveal once (observer disconnects/unobserves after the
 * first intersection), respect prefers-reduced-motion, and degrade safely
 * with no JS (content is never left invisible — see bekra-motion.css).
 */
(() => {
  if (window.__bekraMotionInit) return;
  window.__bekraMotionInit = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Theme Editor: content must stay immediately visible and selectable.
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const OBSERVER_OPTIONS = { threshold: [0, 0.02, 0.05, 0.08, 0.12], rootMargin: '0px 0px -5% 0px' };

  // Reveal once a meaningful part is on screen: 12% of the element OR a
  // visible slice of at least ~160px. The pixel rule makes very tall
  // sections (e.g. a long FAQ hub at the end of a page) reveal even when
  // 12% of them can never be on screen at once.
  const shouldReveal = (entry) =>
    entry.isIntersecting &&
    (entry.intersectionRatio >= 0.12 ||
      entry.intersectionRect.height >= Math.min(160, window.innerHeight * 0.2) ||
      entry.boundingClientRect.top < 0); // already partly scrolled past (top edge above viewport)
  // Skipped past (fast scroll, anchor jump, content inserted after the user
  // already scrolled by): element lies entirely above the viewport. Show it
  // immediately so scrolling back up never reveals an empty area.
  const isAboveViewport = (entry) => !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;

  // Signal "JS is active" as early as possible so bekra-motion.css can key
  // the data-bekra-reveal initial-hidden state off this instead of opacity:0
  // being the default — if this script never runs, the class never appears
  // and content stays fully visible (progressive enhancement).
  document.documentElement.classList.add('bekra-motion-ready');

  // --- 1. <bekra-scroll-reveal> custom element (existing wrapper pattern) ---
  if (!customElements.get('bekra-scroll-reveal')) {
    class BekraScrollReveal extends HTMLElement {
      connectedCallback() {
        if (reducedMotion || designMode) return;

        this.classList.add('js-motion');

        if (!('IntersectionObserver' in window)) {
          this.classList.add('is-visible');
          return;
        }

        this.observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (shouldReveal(entry) || isAboveViewport(entry)) {
              this.classList.add('is-visible');
              this.observer.disconnect();
            }
          }
        }, OBSERVER_OPTIONS);
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }
    }

    customElements.define('bekra-scroll-reveal', BekraScrollReveal);
  }

  // --- 2. [data-bekra-reveal] attribute-based reveal (single shared observer) ---
  if (reducedMotion || designMode || !('IntersectionObserver' in window)) {
    document.querySelectorAll('[data-bekra-reveal]').forEach((el) => {
      el.classList.add('is-visible');
    });
    return;
  }

  const isMobile = () => window.innerWidth < 750;

  // Elements that enter the viewport in the same observer batch (e.g. a row
  // of product cards) get a small stagger: 80ms steps, max 240ms (mobile
  // 60/180ms). Delay is removed again once the reveal has finished so it
  // never affects later hover/state transitions on the same element.
  const attributeObserver = new IntersectionObserver((entries) => {
    entries.filter(isAboveViewport).forEach((entry) => {
      entry.target.classList.add('is-visible');
      attributeObserver.unobserve(entry.target);
    });
    const visible = entries.filter(shouldReveal);
    visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);
    const step = isMobile() ? 60 : 80;
    const cap = isMobile() ? 180 : 240;
    visible.forEach((entry, index) => {
      const el = entry.target;
      const delay = Math.min(index * step, cap);
      if (delay) {
        el.style.transitionDelay = `${delay}ms`;
        setTimeout(() => el.style.removeProperty('transition-delay'), delay + 900);
      }
      el.classList.add('is-visible');
      attributeObserver.unobserve(el);
    });
  }, OBSERVER_OPTIONS);

  const observeRevealElements = (root) => {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    root.querySelectorAll('[data-bekra-reveal]:not(.bekra-motion-observed)').forEach((el) => {
      el.classList.add('bekra-motion-observed');
      attributeObserver.observe(el);
    });
  };

  // --- 3. Automatic reveal for Horizon core sections -------------------------
  // BEKRA sections already reveal via <bekra-scroll-reveal>. Horizon's own
  // sections (collection grid, product recommendations, blog, article,
  // collection header) are tagged here centrally instead of editing their
  // Liquid or any template JSON. Only larger visual units are tagged — whole
  // cards, headline/text blocks, the article image — never single icons,
  // words, buttons or controls.
  const AUTO_TARGETS = [
    ['.product-grid > .product-grid__item', 'card'],
    ['.resource-list--grid > .resource-list__item', 'card'],
    ['.blog-posts-container > .blog-post-item', 'card'],
    ['main .shopify-section .text-block', 'text'],
    ['main .blog-post-featured-image', 'image'],
    ['main .blog-post-content', 'text'],
  ];
  // Never animated: buy area / quick add, header, footer, drawers, dialogs,
  // carousels & slideshows, the hero (has its own CSS load animation) and
  // anything a BEKRA section already reveals itself.
  const AUTO_SKIP =
    '.product-information, header, footer, dialog, cart-drawer, cart-items-component, .cart-page, .customer, sticky-add-to-cart, slideshow-component, .resource-list__carousel, .bekra-hero, bekra-scroll-reveal, [data-bekra-reveal]';
  const CARD_ANCESTORS = '.product-grid__item, .resource-list__item, .blog-post-item';

  const autoTag = (root) => {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    for (const [selector, type] of AUTO_TARGETS) {
      root.querySelectorAll(selector).forEach((el) => {
        if (el.hasAttribute('data-bekra-reveal') || el.closest(AUTO_SKIP)) return;
        if (type === 'text' && el.closest(CARD_ANCESTORS)) return; // card animates as one unit
        // Already on screen when the script runs: leave it visible (no flash).
        const rect = el.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) return;
        el.setAttribute('data-bekra-reveal', type);
      });
    }
    observeRevealElements(root);
  };

  autoTag(document);
  observeRevealElements(document);

  // Product recommendations are fetched after load (skeleton first): tag the
  // real cards once they are inserted, then stop watching.
  document.querySelectorAll('product-recommendations').forEach((el) => {
    const mutationObserver = new MutationObserver(() => {
      if (el.querySelector('.resource-list__item')) {
        mutationObserver.disconnect();
        autoTag(el);
      }
    });
    mutationObserver.observe(el, { childList: true, subtree: true });
  });

  // Back/forward cache: never leave already passed content hidden.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('[data-bekra-reveal]:not(.is-visible)').forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-visible');
    });
  });

  // Theme Editor: when a section is re-rendered (e.g. after a settings
  // change), scan the freshly injected markup for new [data-bekra-reveal]
  // elements. The <bekra-scroll-reveal> custom element needs no equivalent
  // handling — connectedCallback fires automatically on any DOM insertion,
  // including Shopify's section reload.
  document.addEventListener('shopify:section:load', (event) => {
    autoTag(event.target);
  });
})();
